/**
 * SecurityTab — 2FA toggle, active sessions list, audit-log entry point.
 *
 * Session revocation is a destructive action so we gate "Sign out all
 * other sessions" behind a second confirmation click (tap-tap pattern)
 * rather than a blocking modal — see UX Invariant §9 (no modals on
 * tablet-first app).
 */
import { CheckCircle, DeviceMobile, ShieldCheck, SignOut } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { Switch } from '../../../shared/ui/Switch';
import { StatusBadge } from '../../../shared/ui/StatusBadge';
import { cn } from '../../../shared/utils/cn';
import {
  useBusinessSettings,
  useRevokeAllOtherSessions,
  useRevokeSession,
  useUpdateBusinessSettings,
} from '../../../features/settings/useBusinessSettings';
import { SettingsCard } from './SettingsCard';

export function SecurityTab(): JSX.Element {
  const { t, i18n } = useTranslation('settings');
  const settingsQuery = useBusinessSettings();
  const updateMutation = useUpdateBusinessSettings();
  const revokeMutation = useRevokeSession();
  const revokeAllMutation = useRevokeAllOtherSessions();
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [twoFactorSavedAt, setTwoFactorSavedAt] = useState<number | null>(null);

  if (settingsQuery.isLoading || !settingsQuery.data) {
    return <LoadingSkeleton />;
  }

  const { security } = settingsQuery.data;
  const otherSessionCount = security.sessions.filter((s) => !s.isCurrent).length;

  const handleTwoFactorChange = async (checked: boolean) => {
    await updateMutation.mutateAsync({
      security: { ...security, twoFactorEnabled: checked },
    });
    setTwoFactorSavedAt(Date.now());
    window.setTimeout(() => setTwoFactorSavedAt(null), 2500);
  };

  const handleRevokeAll = async () => {
    if (!confirmRevokeAll) {
      setConfirmRevokeAll(true);
      window.setTimeout(() => setConfirmRevokeAll(false), 4000);
      return;
    }
    await revokeAllMutation.mutateAsync();
    setConfirmRevokeAll(false);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div>
      <SettingsCard title={t('security.sectionTitle')} hint={t('security.sectionHint')}>
        <div
          className={cn(
            'flex items-start md:items-center justify-between gap-biz-content-lg',
            'flex-col md:flex-row',
            'py-biz-content-lg border-b border-biz-border',
          )}
        >
          <div className="flex items-start gap-biz-content-lg">
            <ShieldCheck
              weight="regular"
              size={24}
              aria-hidden="true"
              className="text-biz-primary shrink-0 mt-0.5"
            />
            <div>
              <p className="text-biz-base font-medium">{t('security.twoFactorLabel')}</p>
              <p className="text-biz-sm text-biz-muted-foreground">
                {t('security.twoFactorHint')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-biz-content-lg">
            {twoFactorSavedAt ? (
              <span
                role="status"
                className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success"
              >
                <CheckCircle weight="regular" size={18} aria-hidden="true" />
                {t('common.saved')}
              </span>
            ) : null}
            <Switch
              checked={security.twoFactorEnabled}
              onCheckedChange={handleTwoFactorChange}
              disabled={updateMutation.isPending}
              aria-label={t('security.twoFactorLabel')}
            />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title={t('security.sessionsTitle')} hint={t('security.sessionsHint')}>
        <ul className="divide-y divide-biz-border">
          {security.sessions.map((session) => (
            <li
              key={session.id}
              className={cn(
                'flex items-start md:items-center justify-between gap-biz-content-lg',
                'py-biz-content-lg',
                'flex-col md:flex-row',
              )}
            >
              <div className="flex items-start gap-biz-content-lg min-w-0">
                <DeviceMobile
                  weight="regular"
                  size={24}
                  aria-hidden="true"
                  className="text-biz-muted-foreground shrink-0 mt-0.5"
                />
                <div className="min-w-0">
                  <p className="text-biz-base font-medium flex items-center gap-biz-content flex-wrap">
                    <span className="truncate">{session.deviceLabel}</span>
                    {session.isCurrent ? (
                      <StatusBadge
                        variant="success"
                        label={t('security.sessionCurrent')}
                      />
                    ) : null}
                  </p>
                  <p className="text-biz-xs text-biz-muted-foreground">
                    {session.ipAddress} · {formatDate(session.lastActiveAt)}
                  </p>
                </div>
              </div>
              {!session.isCurrent ? (
                <Button
                  type="button"
                  intent="secondary"
                  size="sm"
                  leftIcon={<SignOut size={16} weight="regular" />}
                  loading={
                    revokeMutation.isPending && revokeMutation.variables === session.id
                  }
                  onClick={() => revokeMutation.mutate(session.id)}
                >
                  {t('security.sessionRevoke')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>

        {otherSessionCount > 0 ? (
          <div className="pt-biz-content-lg flex justify-end">
            <Button
              type="button"
              intent={confirmRevokeAll ? 'danger' : 'secondary'}
              loading={revokeAllMutation.isPending}
              onClick={handleRevokeAll}
            >
              {t('security.sessionSignOutAll')}
            </Button>
          </div>
        ) : null}
      </SettingsCard>

      <SettingsCard title={t('security.auditLogTitle')} hint={t('security.auditLogHint')}>
        <div className="flex items-center justify-end">
          <Button intent="secondary" disabled>
            {t('security.auditLogView')}
          </Button>
        </div>
      </SettingsCard>
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-biz-content-lg" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-20 rounded-biz-card bg-biz-muted animate-pulse"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
